import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { clearAllLocalAppData } from "@/lib/startupRecovery";

/**
 * Suspense / loader fallback with built-in recovery.
 *
 * If a chunk load or route bootstrap takes longer than the threshold,
 * we surface explicit recovery actions instead of trapping the user
 * behind an infinite spinner.
 */
export default function SuspenseRecovery({
  timeoutMs = 8000,
  message = "Loading…",
}: {
  timeoutMs?: number;
  message?: string;
}) {
  const [showRecovery, setShowRecovery] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setShowRecovery(true), timeoutMs);
    return () => window.clearTimeout(t);
  }, [timeoutMs]);

  if (!showRecovery) {
    return (
      <div className="flex items-center justify-center h-screen w-full">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="max-w-sm w-full rounded-2xl border border-border bg-card p-6 text-center space-y-4">
        <div className="text-2xl">⚠️</div>
        <div>
          <h1 className="text-base font-semibold">Taking longer than expected</h1>
          <p className="text-xs text-muted-foreground mt-1">
            {message} is taking a while. Try one of the options below.
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            className="h-10 rounded-lg bg-primary text-primary-foreground text-sm font-medium"
            onClick={() => {
              try { window.location.reload(); } catch { /* ignore */ }
            }}
          >
            Reload
          </button>
          <button
            type="button"
            className="h-10 rounded-lg bg-secondary text-foreground text-sm"
            onClick={() => {
              try { window.location.assign("/chats"); } catch { /* ignore */ }
            }}
          >
            Go to chats
          </button>
          <button
            type="button"
            className="h-10 rounded-lg bg-secondary text-foreground text-sm"
            onClick={() => {
              try { window.location.assign("/"); } catch { /* ignore */ }
            }}
          >
            Go to home
          </button>
          <button
            type="button"
            disabled={resetting}
            className="h-10 rounded-lg border border-border text-sm text-destructive disabled:opacity-50"
            onClick={async () => {
              setResetting(true);
              try { await clearAllLocalAppData(); } catch { /* ignore */ }
              try { window.location.reload(); } catch { /* ignore */ }
            }}
          >
            {resetting ? "Resetting…" : "Reset local data & reload"}
          </button>
        </div>
      </div>
    </div>
  );
}
